// ── Hex / RGB helpers ─────────────────────────────────────────────────────────

/**
 * Parse a hex color string to [r, g, b] (0–255).
 * Accepts 3- or 6-digit hex with or without leading #.
 */
export function hexToRgb(hex) {
  if (!hex) return [0, 0, 0]
  const clean = hex.replace('#', '')
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b]
    .map(c => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
    .join('')
}

function hslToHex(h, s, l) {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = n => {
    const k = (n + h / 30) % 12
    return (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))) * 255
  }
  return rgbToHex(f(0), f(8), f(4))
}

/**
 * Mix a hex color with black or white by percentage (0–100).
 * Mirrors CSS color-mix(in srgb, color pct%, target).
 */
export function mixHex(hex, pct, target = '#000000') {
  const [r1, g1, b1] = hexToRgb(hex)
  const [r2, g2, b2] = hexToRgb(target)
  const p = pct / 100
  return rgbToHex(
    r1 * p + r2 * (1 - p),
    g1 * p + g2 * (1 - p),
    b1 * p + b2 * (1 - p),
  )
}

/**
 * Extract hue (0–360) and saturation (0–100) from a hex color.
 */
export function getHueSat(hex) {
  const [r, g, b] = hexToRgb(hex).map(c => c / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  const l = (max + min) / 2

  if (d === 0) return { hue: 0, sat: 0 }

  const sat = d / (1 - Math.abs(2 * l - 1)) * 100
  let hue
  if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) * 60
  else if (max === g) hue = ((b - r) / d + 2) * 60
  else hue = ((r - g) / d + 4) * 60

  return { hue: Math.round(hue) % 360, sat: Math.round(sat) }
}

// ── WCAG contrast ─────────────────────────────────────────────────────────────

function linearize(c) {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

/**
 * Relative luminance per WCAG 2.1 (0 = black, 1 = white).
 */
export function getLuminance(hex) {
  const [r, g, b] = hexToRgb(hex)
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)
}

/**
 * WCAG contrast ratio between two hex colors.
 * Thresholds: ≥ 4.5 normal text, ≥ 3.0 large text / UI components.
 */
export function getContrastRatio(hex1, hex2) {
  const l1 = getLuminance(hex1)
  const l2 = getLuminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker  = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

export const DARK_TEXT = '#212529'
export const LIGHT_TEXT = '#ffffff'

// Dark or white text, whichever reads better on this color
export function readableTextOn(bgHex) {
  return getContrastRatio(DARK_TEXT, bgHex) > getContrastRatio(LIGHT_TEXT, bgHex) ? DARK_TEXT : LIGHT_TEXT
}

// Below this ratio a color pair can't be saved; between it and the pair's threshold, saving warns
export const CONTRAST_FLOOR = 3
export const TEXT_CONTRAST_THRESHOLD = 4.5

// Theme text/background pairs checked for readability
export const THEME_CONTRAST_PAIRS = [
  { fg: 'textColor', bg: 'bgColor', fgLabel: 'Main text', bgLabel: 'Page background', threshold: 5.5 },
  { fg: 'headerTextColor', bg: 'headerColor', fgLabel: 'Header text', bgLabel: 'Header background', threshold: TEXT_CONTRAST_THRESHOLD },
  { fg: 'footerTextColor', bg: 'footerColor', fgLabel: 'Footer text', bgLabel: 'Footer background', threshold: TEXT_CONTRAST_THRESHOLD },
  { fg: 'highlightColor', bg: 'bgColor', fgLabel: 'Accents', bgLabel: 'Page background', threshold: TEXT_CONTRAST_THRESHOLD },
]

export const MAIN_TEXT_CONTRAST_THRESHOLD = THEME_CONTRAST_PAIRS[0].threshold

// 'unreadable' below the floor, 'weak' below the threshold, otherwise null
export function contrastLevel(fg, bg, threshold = TEXT_CONTRAST_THRESHOLD) {
  const ratio = getContrastRatio(fg, bg)
  if (ratio < CONTRAST_FLOOR) return 'unreadable'
  return ratio < threshold ? 'weak' : null
}

// Theme pairs below their threshold, each with its level
export function themeContrastIssues(theme) {
  return THEME_CONTRAST_PAIRS
    .map(pair => ({ ...pair, level: contrastLevel(theme[pair.fg], theme[pair.bg], pair.threshold) }))
    .filter(pair => pair.level)
}

// Message parts with the color names in bold, for TextParts
export function unreadableReason(fgLabel, bgLabel) {
  return [{ text: fgLabel, strong: true }, { text: " can't be read on " }, { text: bgLabel, strong: true }]
}

// Message for a pair below its threshold: 'error' blocks saving, 'warning' doesn't
export function contrastPairMessage(fgLabel, bgLabel, level) {
  if (level === 'unreadable') {
    return { type: 'error', parts: [...unreadableReason(fgLabel, bgLabel), { text: ' - fix it to save.' }] }
  }
  return { type: 'warning', parts: [{ text: fgLabel, strong: true }, { text: ' is hard to read on ' }, { text: bgLabel, strong: true }, { text: '.' }] }
}

/** True when neither white nor black text clears the threshold on this background. */
export function isDeadBandBackground(bgHex, threshold = 5.5) {
  return getContrastRatio(bgHex, '#ffffff') < threshold
    && getContrastRatio(bgHex, '#000000') < threshold
}

/**
 * Nearest lighter (paired with black text) and darker (paired with white
 * text) shades that clear the threshold. Either can be null if unreachable.
 */
export function suggestReadableShades(bgHex, threshold = 5.5) {
  const STEP = 4
  let light = null
  let dark = null

  for (let pct = 100 - STEP; pct >= 0; pct -= STEP) {
    const candidate = mixHex(bgHex, pct, '#ffffff')
    if (getContrastRatio(candidate, '#000000') >= threshold) {
      light = { bg: candidate, text: '#000000' }
      break
    }
  }

  for (let pct = 100 - STEP; pct >= 0; pct -= STEP) {
    const candidate = mixHex(bgHex, pct, '#000000')
    if (getContrastRatio(candidate, '#ffffff') >= threshold) {
      dark = { bg: candidate, text: '#ffffff' }
      break
    }
  }

  return { light, dark }
}

/**
 * Darken a hex color by pct % (0–100). Used as a JS fallback where
 * CSS color-mix() is unavailable.
 */
export function darkenHex(hex, pct) {
  const factor = 1 - pct / 100
  const [r, g, b] = hexToRgb(hex)
  return rgbToHex(r * factor, g * factor, b * factor)
}

export const SUCCESS_HUE = 140
export const ERROR_HUE = 5

// Success/error keep their hue but borrow the accent's saturation, darkened until
// readable on their light message box and under white text
export function deriveStatusColor(hue, accentHex) {
  const sat = accentHex ? Math.min(70, Math.max(35, getHueSat(accentHex).sat)) : 65
  for (let l = 42; l > 15; l -= 2) {
    const hex = hslToHex(hue, sat, l)
    if (getContrastRatio(hex, mixHex(hex, 12, '#ffffff')) >= 4.5
      && getContrastRatio('#ffffff', hex) >= 4.5) return hex
  }
  return hslToHex(hue, sat, 15)
}

// ── Font presets ──────────────────────────────────────────────────────────────

export const FONT_PRESETS = {
  classic:  { decorative: 'Prata',               primary: 'Open Sans' },
  elegant:  { decorative: 'Playfair Display',    primary: 'Lato' },
  literary: { decorative: 'Cormorant Garamond',  primary: 'Source Sans 3' },
  modern:   { decorative: 'Inter',               primary: 'Inter' },
  artisan:  { decorative: 'Libre Baskerville',   primary: 'Open Sans' },
  studio:   { decorative: 'DM Serif Display',    primary: 'DM Sans' },
}

// Individual font definitions for the custom font picker. Fonts are self-hosted
// (public/fonts/, generated by scripts/fetch-fonts.mjs): adding a font here also
// means adding its files there and to FONT_FILES in public/index.html.
export const HEADING_FONTS = [
  { name: 'Prata' },
  { name: 'Playfair Display' },
  { name: 'Cormorant Garamond' },
  { name: 'Inter' },
  { name: 'Libre Baskerville' },
  { name: 'DM Serif Display' },
]

export const BODY_FONTS = [
  { name: 'Open Sans' },
  { name: 'Lato' },
  { name: 'Source Sans 3' },
  { name: 'Inter' },
  { name: 'DM Sans' },
  { name: 'Nunito' },
  { name: 'Raleway' },
]

// Curated compatible body fonts per heading font (canonical pairing listed first).
// Canonical preset body (matching the font card) is listed last so manual selectors
// and auto-reset default to a non-preset pairing.
export const FONT_PAIRINGS = {
  'Prata':              ['Lato', 'Nunito', 'Source Sans 3', 'Raleway', 'Open Sans'],
  'Playfair Display':   ['Raleway', 'Source Sans 3', 'Inter', 'Nunito', 'Lato'],
  'Cormorant Garamond': ['Raleway', 'DM Sans', 'Open Sans', 'Nunito', 'Source Sans 3'],
  'Inter':              ['Open Sans', 'Lato', 'DM Sans', 'Source Sans 3', 'Inter'],
  'Libre Baskerville':  ['Nunito', 'Lato', 'Source Sans 3', 'Raleway', 'Open Sans'],
  'DM Serif Display':   ['Inter', 'Raleway', 'Source Sans 3', 'Nunito', 'DM Sans'],
}

// ── Theme presets ─────────────────────────────────────────────────────────────

export const THEME_PRESETS = [
  {
    key:          'blanc',
    label:        'Blanc',
    bgColor:      '#f9f9f8',
    textColor:    '#1a1a1a',
    headerColor:  '#222222',
    headerTextColor: '#E9E9E9',
    footerColor:  '#f0efec',
    footerTextColor: '#666666',
    accentColor:  '#10c468',
    highlightColor: '#c04400',
    fontHeading:  'Prata',
    fontBody:     'Open Sans',
  },
  {
    key:          'terracotta',
    label:        'Terracotta',
    bgColor:      '#faf7f4',
    textColor:    '#2d1f18',
    headerColor:  '#e8d5c4',
    headerTextColor: '#3d2a1e',
    footerColor:  '#dcc9b6',
    footerTextColor: '#5a4035',
    accentColor:  '#946e49',
    highlightColor: '#b84f28',
    fontHeading:  'Libre Baskerville',
    fontBody:     'Open Sans',
  },
  {
    key:          'cobalt',
    label:        'Cobalt',
    bgColor:      '#f5f8ff',
    textColor:    '#0d1b2a',
    headerColor:  '#1a3a5c',
    headerTextColor: '#e0e8f0',
    footerColor:  '#162f4a',
    footerTextColor: '#b0c4de',
    accentColor:  '#4a9eff',
    highlightColor: '#a85c2c',
    fontHeading:  'Inter',
    fontBody:     'Inter',
  },
  {
    key:          'dusk',
    label:        'Dusk',
    bgColor:      '#f7f5f9',
    textColor:    '#1a1525',
    headerColor:  '#2d2440',
    headerTextColor: '#e0d8ee',
    footerColor:  '#251e36',
    footerTextColor: '#b8aed0',
    accentColor:  '#7c5cbf',
    highlightColor: '#a15b38',
    fontHeading:  'DM Serif Display',
    fontBody:     'DM Sans',
  },
  {
    key:          'obsidian',
    label:        'Obsidian',
    bgColor:      '#121212',
    textColor:    '#e8e8e8',
    headerColor:  '#0d0d0d',
    headerTextColor: '#e0e0e0',
    footerColor:  '#0d0d0d',
    footerTextColor: '#a0a0a0',
    accentColor:  '#7eb8f7',
    highlightColor: '#f0a050',
    fontHeading:  'Playfair Display',
    fontBody:     'Lato',
  },
]

// ── Random theme generator ────────────────────────────────────────────────────

const MOODS = ['light', 'warm', 'dark', 'bold', 'dusk', 'vivid']

// Maps mood → font preset keys whose decorative font suits that mood's character.
const MOOD_FONTS = {
  light: ['classic', 'modern', 'studio'],
  warm:  ['artisan', 'classic', 'studio'],
  dark:  ['elegant', 'literary', 'studio'],
  bold:  ['modern', 'elegant'],
  dusk:  ['literary', 'elegant'],
  vivid: ['modern', 'studio'],
}


function buildThemeForMood(hue, mood) {
  switch (mood) {
    case 'light':
      return {
        bgColor:      hslToHex(hue,  15, 97),
        textColor:    hslToHex(hue,  20, 15),
        headerColor:  hslToHex(hue,  25, 92),
        headerTextColor: hslToHex(hue, 20, 20),
        footerColor:  hslToHex(hue,  25, 87),
        footerTextColor: hslToHex(hue, 15, 30),
        accentColor:    hslToHex(hue,  70, 42),
        highlightColor: hslToHex(hue,  70, 42),
      }
    case 'warm':
      return {
        bgColor:        hslToHex((hue + 20) % 360, 22, 96),
        textColor:      hslToHex(25,               35, 18),
        headerColor:    hslToHex((hue + 20) % 360, 30, 88),
        headerTextColor: hslToHex(25,              30, 22),
        footerColor:    hslToHex((hue + 20) % 360, 28, 82),
        footerTextColor: hslToHex(25,              25, 28),
        accentColor:    hslToHex(hue,              68, 40),
        highlightColor: hslToHex((hue + 30) % 360, 65, 42),
      }
    case 'dark':
      return {
        bgColor:        hslToHex(hue, 28, 10),
        textColor:      hslToHex(hue, 10, 90),
        headerColor:    hslToHex(hue, 25,  7),
        headerTextColor: hslToHex(hue, 10, 88),
        footerColor:    hslToHex(hue, 25,  7),
        footerTextColor: hslToHex(hue, 8, 70),
        accentColor:    hslToHex(hue, 75, 65),
        highlightColor: hslToHex((hue + 40) % 360, 70, 70),
      }
    case 'bold':
      return {
        bgColor:        hslToHex(hue,               5, 98),
        textColor:      hslToHex(hue,              10, 10),
        headerColor:    hslToHex(hue,              80, 42),
        headerTextColor: hslToHex(hue,              5, 95),
        footerColor:    hslToHex(hue,              72, 32),
        footerTextColor: hslToHex(hue,              5, 90),
        accentColor:    hslToHex((hue + 180) % 360, 72, 44),
        highlightColor: hslToHex(hue,              75, 45),
      }
    case 'dusk':
      return {
        bgColor:        hslToHex(hue, 28, 20),
        textColor:      hslToHex(hue,  8, 88),
        headerColor:    hslToHex(hue, 30, 14),
        headerTextColor: hslToHex(hue,  8, 84),
        footerColor:    hslToHex(hue, 30, 14),
        footerTextColor: hslToHex(hue,  8, 68),
        accentColor:    hslToHex(hue, 72, 65),
        highlightColor: hslToHex((hue + 35) % 360, 65, 76),
      }
    case 'vivid':
      return {
        bgColor:         hslToHex(hue, 82, 74),
        textColor:       hslToHex(hue, 20,  8),
        headerColor:     hslToHex(hue, 82, 74),  // matches page bg
        headerTextColor: hslToHex(hue, 20,  8),
        footerColor:     hslToHex(hue, 82, 74),  // matches page bg
        footerTextColor: hslToHex(hue, 20, 10),
        accentColor:     hslToHex(hue, 85, 32),
        highlightColor:  hslToHex(hue, 40, 10),
      }
  }
}

function isContrastSafe(theme) {
  const { accentColor } = theme
  if (THEME_CONTRAST_PAIRS.some(p => getContrastRatio(theme[p.fg], theme[p.bg]) < p.threshold)) return false
  // The button text applyTheme will pick must meet ≥ 4.5
  if (getContrastRatio(readableTextOn(accentColor), accentColor) < TEXT_CONTRAST_THRESHOLD) return false
  // Generated themes avoid red-toned buttons
  const accentHS = getHueSat(accentColor)
  if (accentHS.sat >= 30 && (accentHS.hue <= 15 || accentHS.hue >= 345)) return false
  return true
}

/**
 * Generate contrast-safe random colors. Returns the colors and the mood
 * used to generate them — mood is optional for the caller to use or ignore.
 * @returns {{ colors: object, mood: string }}
 */
export function generateRandomColors() {
  const MAX_ATTEMPTS = 20
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const hue  = Math.floor(Math.random() * 360)
    const mood = MOODS[Math.floor(Math.random() * MOODS.length)]
    const colors = buildThemeForMood(hue, mood)
    if (isContrastSafe(colors)) return { colors, mood }
  }
  const { key: _k, label: _l, fontHeading: _fh, fontBody: _fb, ...colors } = THEME_PRESETS[0]
  return { colors, mood: 'light' }
}

/**
 * Generate a random font pairing from FONT_PAIRINGS.
 * When mood is provided, the heading font is drawn from MOOD_FONTS for coherence.
 * @param {string|null} mood
 * @returns {{ fontHeading: string, fontBody: string }}
 */
export function generateRandomFontPair(mood = null) {
  // Exclude pairs that match a curated preset card so random never lands on one
  const presetPairs = new Set(Object.values(FONT_PRESETS).map(p => `${p.decorative}|${p.primary}`))

  let headingName
  if (mood && MOOD_FONTS[mood]) {
    const presetKey = MOOD_FONTS[mood][Math.floor(Math.random() * MOOD_FONTS[mood].length)]
    headingName = FONT_PRESETS[presetKey].decorative
  } else {
    const headings = Object.keys(FONT_PAIRINGS)
    headingName = headings[Math.floor(Math.random() * headings.length)]
  }

  const pool = FONT_PAIRINGS[headingName].filter(body => !presetPairs.has(`${headingName}|${body}`))
  const bodyOptions = pool.length ? pool : FONT_PAIRINGS[headingName]
  return { fontHeading: headingName, fontBody: bodyOptions[Math.floor(Math.random() * bodyOptions.length)] }
}

/**
 * Generate a full coherent random theme: mood-matched colors and fonts together.
 * @returns {{ bgColor, ..., fontHeading, fontBody }}
 */
export function generateRandomTheme() {
  const { colors, mood } = generateRandomColors()
  return { ...colors, ...generateRandomFontPair(mood) }
}
